import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { loginUser, registerUser } from '../utils/supabaseEdgeApi';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const [token, setToken] = useState(() => localStorage.getItem('supabaseToken') || '');
  const [user, setUser] = useState(() => {
    const saved = localStorage.getItem('supabaseUser');
    return saved ? JSON.parse(saved) : null;
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (token) {
      localStorage.setItem('supabaseToken', token);
    } else {
      localStorage.removeItem('supabaseToken');
    }
  }, [token]);

  useEffect(() => {
    if (user) {
      localStorage.setItem('supabaseUser', JSON.stringify(user));
    } else {
      localStorage.removeItem('supabaseUser');
    }
  }, [user]);

  const logout = useCallback(() => {
    setToken('');
    setUser(null);
    setError('');
  }, []);

  const redirectToLogin = useCallback(() => {
    logout();
    navigate('/login');
  }, [logout, navigate]);

  const login = useCallback(async ({ email, password }) => {
    setLoading(true);
    setError('');
    try {
      const response = await loginUser({ email, password });
      setToken(response.token);
      const { name, id, email: emailV, address, contact } = response.user;
      setUser({
        id,
        name,
        email: emailV,
        contact,
        address,
      });
      return response;
    } catch (fetchError) {
      const message = fetchError?.message || 'Login failed';
      setError(message);
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  }, []);

  const register = useCallback(async ({ name, email, password, contact, address }) => {
    setLoading(true);
    setError('');
    try {
      const response = await registerUser({ name, email, password, contact, address });
      setToken(response.token);
      setUser({
        id: response.userId,
        name: response.name || name,
        email,
        contact: response.contact || contact || '',
        address: response.address || address || '',
      });
      return response;
    } catch (fetchError) {
      const message = fetchError?.message || 'Registration failed';
      setError(message);
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  }, []);

  const value = useMemo(
    () => ({ user, token, loading, error, login, register, logout, redirectToLogin, setUser }),
    [user, token, loading, error, login, register, logout, redirectToLogin]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
